import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

type ThemeContextValue = {
  darkMode: boolean
  setDarkMode: (value: boolean) => void
  toggleDarkMode: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'pkt-theme-dark-mode'

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches

  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored !== null) return stored === 'true'
    return prefersDark
  })

  // Apply attribute + persist
  useEffect(() => {
    const root = document.documentElement // <html>

    if (darkMode) {
      root.setAttribute('data-mode', 'dark')
    } else {
      root.removeAttribute('data-mode')
    }

    sessionStorage.setItem(STORAGE_KEY, String(darkMode))
  }, [darkMode])

  const value = useMemo(
    () => ({
      darkMode,
      setDarkMode,
      toggleDarkMode: () => setDarkMode((v) => !v),
    }),
    [darkMode],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
